<template>
    <NuxtLayout name="main" :dark="true" :top="0">
        <ModuleTutorial ref="tutorial" :mode="mode" />
        <div :class="css.container">
            <div :class="css.header">
                <!-- * 标题 -->
                <div :class="css['header-title']" class="module">
                    <div class="flex items-end">
                        <h1 class="font-variable font-bold color-1" :class="css.name">{{ options.name }}</h1>
                        <div>
                            <i18n-t keypath="HowToUse" scope="global">
                                <template v-slot:view>
                                    <span :class="css_normalize.assets" @click="tutorial?.open">{{ $t("EasyGuide") }}</span>
                                </template>
                            </i18n-t>
                        </div>
                    </div>
                    <Favorites />
                </div>

                <!-- * 操作信息 -->
                <ui-form ref="form" @submit.prevent="() => {}" :class="css.controls" :data="keywords" :rules="rules">
                    <!-- * 高亮相同项 -->
                    <div :class="[css.control, css.checkbox]">
                        <ui-checkbox v-model="checkbox" :value="$t('HighlightTheSameItems')" @change="methods.comparison" />
                    </div>

                    <div :class="css.control" v-for="(value, index) in keywords" :key="index">
                        <!-- * 已添加的搜索项 -->
                        <div :class="css.module" v-if="data[index]">
                            <ui-tooltip-follow :zIndex="666" :classExtraName="css.tip" class="ellipsis">
                                <ProjectRoute :class="css.name" class="color-1" :to="{ name: options.route, params: { id: data[index].id } }">
                                    <ui-icon :class="css.specie" :name="useSpeciesIconKey(data[index].specie)" />
                                    <span class="font-variable font-bold" :class="css.gene" v-html="data[index].name"></span>
                                </ProjectRoute>
                                <template #content>
                                    <span style="font-size: 12px" v-html="data[index].name"></span>
                                </template>
                            </ui-tooltip-follow>
                            <ui-icon name="close" :class="css.close" @click="methods.clear(index)" />
                        </div>
                        <ui-form-item :class="css.input" :prop="`search${index}`" v-else>
                            <default-search
                                v-model="keywords[index].value"
                                :type="mode"
                                :classExtraName="css.candidates"
                                :placeholder="options.placeholder"
                                :maxlength="1024"
                                :zIndex="666"
                                width="100%"
                                mode="select"
                                size="small"
                                @search="(data) => methods.select(data, index)" />
                        </ui-form-item>
                    </div>
                </ui-form>
            </div>

            <div :class="css.modules">
                <div :class="css.module" v-for="(option, key) in options.option" :key="key">
                    <!-- * 表格名称 -->
                    <div :class="css.title">
                        <div class="font-variable font-bold color-1" :class="css.name">
                            <h2>{{ option.name }}</h2>
                            <helper-modal
                                v-if="option.explain"
                                :class="css.helper"
                                :content="option.explain"
                                :zIndex="667"
                                :width="360"
                                :size="16"
                                :icon="12"
                                theme="grey" />
                        </div>
                        <ui-icon :class="css.visible" :name="option.visible ? 'subtract' : 'add'" @click="option.visible = !option.visible" />
                    </div>

                    <!-- * 表格内容 -->
                    <Transition v-on="ons.animations">
                        <div :class="css.content" v-if="option.visible && option.option?.length">
                            <div v-for="row in option.option" :class="[css.rowspan, row.same && checkbox && css.active]">
                                <!-- * 列名 -->
                                <div :class="css.name">
                                    <h3 class="font-normal">{{ row.name }}</h3>
                                    <helper-modal
                                        v-if="row.explain"
                                        :class="css.helper"
                                        :content="row.explain"
                                        :zIndex="667"
                                        :width="360"
                                        :size="16"
                                        :icon="12"
                                        theme="grey" />
                                </div>

                                <!-- * 内容 -->
                                <div :class="css.colspan" v-for="(value, key) in data" :key="key">
                                    <!-- * 空数据 -->
                                    <template v-if="!value"></template>
                                    <!-- * 同源基因 -->
                                    <template v-else-if="row.key == 'orthologs'">
                                        <template v-if="!value?.[option.key]?.orthologs?.length"> -- </template>
                                        <div :class="css.orthologs" v-else>
                                            <!-- 跳转至对应基因的详情页面 -->
                                            <template v-for="v in value[option.key].orthologs" :key="v.id">
                                                <ProjectRoute :class="[css.ortholog, css[v.specie]]" :to="{ name: 'Gene', params: { id: v.id } }">
                                                    <ui-icon :class="css.icon" :name="useSpeciesIconKey(v.specie)" />
                                                    <span :class="css_normalize.assets">{{ v.name }}</span>
                                                </ProjectRoute>
                                            </template>
                                        </div>
                                    </template>

                                    <!-- * 转录本id -->
                                    <template v-else-if="row.key == 'transcript_id'">
                                        <template v-if="!value?.[option.key]?.transcript_id"> -- </template>
                                        <!-- * 打开对应基因详情页，并跳转至的转录本模块的表格信息部分 -->
                                        <template v-else>
                                            <ProjectRoute :class="css.mrna" :to="{ name: 'Gene', params: { id: value.id }, query: row.query }">
                                                <span :class="css_normalize.assets" class="ellipsis">{{ value[option.key].transcript_id }}</span>
                                                <img
                                                    src="https://rdgta.cdn.cyagen.com/rddc/pictures/649e26c9-eaaa-428d-9d26-7e20c29609d3.webp"
                                                    alt="Mane" />
                                            </ProjectRoute>
                                        </template>
                                    </template>

                                    <!-- * 解剖分类 -->
                                    <template v-else-if="row.key == 'anatomical_category'">
                                        <template v-if="!value?.[option.key]?.anatomical_category?.length"> -- </template>
                                        <div :class="css.anatomicals" v-else>
                                            <template v-for="anatomical in value[option.key].anatomical_category" :key="anatomical.name">
                                                <ui-tooltip-follow :class="css.anatomical" :content="anatomical.name" :zIndex="667">
                                                    <img :src="anatomical.icon" :alt="anatomical.name" />
                                                </ui-tooltip-follow>
                                            </template>
                                        </div>
                                    </template>

                                    <!-- * 带链接和带View ALL（必须存在route配置） -->
                                    <template v-else-if="vars.rowspan_views_link.includes(row.key)">
                                        <template v-if="!value?.[option.key]?.[row.key]?.length"> -- </template>
                                        <div :class="css.relateds" v-else>
                                            <!-- * 跳转至对应的详情页 -->
                                            <template v-for="v in value[option.key][row.key]" :key="v.id">
                                                <ui-tooltip-follow :class="css.related" :zIndex="999" :classExtraName="css.tip">
                                                    <template #content><span style="font-size: 12px" v-html="v.name"></span></template>
                                                    <template #default>
                                                        <template v-if="row.route">
                                                            <ProjectRoute class="ellipsis" :to="{ name: row.route, params: { id: v.id } }">
                                                                <ui-icon v-if="v.specie" :class="css[v.specie]" :name="useSpeciesIconKey(v.specie)" />
                                                                <span :class="css_normalize.assets" v-html="v.name"></span>
                                                                <span v-if="v.number"> ({{ v.number }})</span>
                                                            </ProjectRoute>
                                                        </template>
                                                        <template v-else>
                                                            <div class="ellipsis">
                                                                <span :class="css_normalize.assets" v-html="v.name"></span>
                                                            </div>
                                                        </template>
                                                    </template>
                                                </ui-tooltip-follow>
                                            </template>
                                            <!-- * 跳转到详情页面中相关部分 -->
                                            <ProjectRoute
                                                :class="[css.related, css.views]"
                                                :to="{ name: options.route, params: { id: value.id }, query: row.query }">
                                                {{ $t("ViewAll") }}
                                            </ProjectRoute>
                                        </div>
                                    </template>

                                    <!-- * 不带链接和带View ALL -->
                                    <template v-else-if="vars.rowspan_views.includes(row.key)">
                                        <template v-if="!value?.[option.key]?.[row.key]?.length"> -- </template>
                                        <div :class="css.relateds" v-else>
                                            <!-- * 跳转至对应的详情页 -->
                                            <template v-for="v in value[option.key][row.key]" :key="v.id">
                                                <ui-tooltip-follow :class="css.related" :zIndex="999" :classExtraName="css.tip">
                                                    <template #content><span style="font-size: 12px" v-html="v"></span></template>
                                                    <template #default>
                                                        <span class="ellipsis">{{ v }}</span>
                                                    </template>
                                                </ui-tooltip-follow>
                                            </template>
                                            <!-- * 跳转到详情页面中相关部分 -->
                                            <ProjectRoute
                                                :class="[css.related, css.views]"
                                                :to="{ name: options.route, params: { id: value.id }, query: row.query }">
                                                {{ $t("ViewAll") }}
                                            </ProjectRoute>
                                        </div>
                                    </template>

                                    <!-- * 不带链接和不带View -->
                                    <template v-else-if="vars.rowspan_nots.includes(row.key)">
                                        <template v-if="!value?.[option.key]?.[row.key]?.length"> -- </template>
                                        <div :class="css.relateds" v-else>
                                            <template v-for="v in value?.[option.key]?.[row.key]" :key="v">
                                                <ui-tooltip-follow :class="css.related" :zIndex="999" :classExtraName="css.tip">
                                                    <template #content><span style="font-size: 12px" v-html="v"></span></template>
                                                    <template #default>
                                                        <span class="ellipsis">{{ v }}</span>
                                                    </template>
                                                </ui-tooltip-follow>
                                            </template>
                                        </div>
                                    </template>

                                    <template v-else>
                                        {{ value?.[option.key]?.[row.key] || "--" }}
                                    </template>
                                </div>
                            </div>
                        </div>
                    </Transition>
                </div>
            </div>
        </div>
    </NuxtLayout>
</template>

<script lang="ts" setup>
//* 组件库
import { UiCheckbox, UiIcon, UiTooltipFollow, UiFormItem, UiForm } from "various-ui";
//* 公共组件引入
import { DefaultSearch, Favorites } from "@/components/controls";
import { HelperModal } from "@/components/windows";

//* 私有组件
import ModuleTutorial from "./components/tutorial/index.vue";
//* 组件属性
import { useComposable } from "./src/composable";
//* 样式
import css from "./style/index.module.less";
import css_normalize from "@/assets/style/src/index.module.less";

//* 组合函数
const { ons, vars, refs, mode, nodes, rules, methods, options, keywords } = useComposable();
const { data, checkbox } = refs;
const { form, tutorial } = nodes;

//* 页面初始化
onMounted(() => methods.init());
</script>
